import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Typography } from "@mui/joy";
import { Box } from "@mui/system";
import { HomeFilledSharp } from "@mui-symbols-material/w300";
import { BreadcrumbsRoute } from "use-react-router-breadcrumbs";
import { Link, useNavigate } from "react-router-dom";
import { TFunction } from "i18next";
import {
    AssetNameCrumb,
    SystemNameCrumb,
    AttributeNameCrumb,
} from "./DynamicCrumbs";
import { isContaminationDeclarationsEnabled } from "../../utils/contaminationDeclarationsEnabled";

/**
 * Centralized breadcrumb configuration for all routes
 * Uses translation function for static labels and component references for dynamic content
 */
export const breadcrumbConfig = (t: TFunction): BreadcrumbsRoute[] => [
    // Profile routes
    { path: "/profile", breadcrumb: t("Profile") },
    { path: "/profile/information", breadcrumb: t("Information") },
    { path: "/profile/notifications", breadcrumb: t("Notifications") },
    { path: "/profile/notificationSettings", breadcrumb: t("Notification Settings") },
    { path: "/profile/notificationSettings/preferences", breadcrumb: t("Preferences") },
    { path: "/profile/notificationSettings/installedBase", breadcrumb: t("Installed Base Subscriptions") },
    { path: "/profile/notificationSettings/iotReports", breadcrumb: t("IoT Report Subscriptions") },
    { path: "/profile/about", breadcrumb: t("About") },

    // Asset routes
    { path: "/assets", breadcrumb: t("Assets") },
    { path: "/assets/:assetLocationId", breadcrumb: AssetNameCrumb },
    { path: "/assets/:assetLocationId/dashboard", breadcrumb: t("Dashboard") },
    { path: "/assets/:assetLocationId/specifications", breadcrumb: t("Specifications") },
    { path: "/assets/:assetLocationId/iot", breadcrumb: t("IoT Connection") },
    { path: "/assets/:assetLocationId/files", breadcrumb: t("Files") },
    { path: "/assets/:assetLocationId/events", breadcrumb: t("Event History") },
    // Feature-flagged (build-time constant, see src/utils/contaminationDeclarationsEnabled.ts).
    ...(isContaminationDeclarationsEnabled()
        ? [{ path: "/assets/:assetLocationId/contaminationDeclarations", breadcrumb: t("Contamination Declarations") }]
        : []),
    // camelCase segments need an explicit entry: the automatic crumb label does
    // not split camelCase ("ServiceReminders").
    { path: "/assets/:assetLocationId/serviceReminders", breadcrumb: t("Service Reminders") },
    { path: "/assets/:assetLocationId/conditionMonitoring", breadcrumb: t("Condition Monitoring") },
    { path: "/assets/:assetLocationId/conditionMonitoring/telemetry", breadcrumb: t("Telemetry") },
    { path: "/assets/:assetLocationId/conditionMonitoring/maintenance", breadcrumb: t("Maintenance") },
    { path: "/assets/:assetLocationId/conditionMonitoring/slaReport", breadcrumb: t("SLA Report") },

    // System routes
    { path: "/systems", breadcrumb: t("Systems") },
    { path: "/systems/:systemId", breadcrumb: SystemNameCrumb },
    { path: "/systems/:systemId/dashboard", breadcrumb: t("Dashboard") },
    { path: "/systems/:systemId/files", breadcrumb: t("Files") },
    { path: "/systems/:systemId/iotEdit", breadcrumb: t("Edit IoT System") },

    // Configuration routes
    { path: "/configurations", breadcrumb: t("Configurations") },

    // Attributes
    { path: "/configurations/attributes", breadcrumb: t("Attributes") },
    { path: "/configurations/attributes/:attributeId", breadcrumb: AttributeNameCrumb },
    { path: "/configurations/attributes/:attributeId/information", breadcrumb: t("Information") },
    { path: "/configurations/attributes/:attributeId/optionValues", breadcrumb: t("Option values") },
];

export interface BreadcrumbMatch {
    match: {
        pathname: string;
    };
    breadcrumb: React.ReactNode;
    key: string;
    noLink?: boolean;
    linkTo?: string;
}

interface BreadcrumbsProps {
    breadcrumbs: BreadcrumbMatch[];
    sx?: object;
}

// Number of leading items that always stay visible: Home icon, the section
// ("Assets"/"Systems") and the client. Collapsing starts at the element after
// the client.
const FRONT_COUNT = 3;
// Number of trailing items that always stay visible: the asset/system name and
// the current page (e.g. "Dashboard").
const END_COUNT = 2;
// Small reserve so we collapse slightly before hitting the hard edge.
const WIDTH_RESERVE = 8;

// Horizontal padding instead of margin so the separator's full footprint is
// captured by offsetWidth during measurement.
const separatorSx = { px: '6px', color: '#666', flexShrink: 0, userSelect: 'none' as const };

/**
 * Unified Breadcrumbs component.
 *
 * Renders navigation breadcrumbs on a single line. When the full trail does not
 * fit the available width, the middle items are collapsed into a clickable "…"
 * starting with the element right after the client and proceeding one element at
 * a time toward the asset/system name. The head (Home / section / client) and
 * the tail (asset name / current page) always stay visible. Clicking the "…"
 * expands the full trail (allowed to wrap).
 *
 * Visible item count is derived from real widths via a hidden measurement row +
 * a ResizeObserver, so it adapts to both viewport size and async-resolved crumb
 * titles. Mirrors the approach in InstalledBaseNodePathBreadcrumb.
 */
const Breadcrumbs = ({ breadcrumbs, sx }: BreadcrumbsProps) => {
    const navigate = useNavigate();

    // Filter out the root "/" breadcrumb if present
    const filteredBreadcrumbs = breadcrumbs.filter(
        ({ match }) => match.pathname !== "/"
    );

    // Build a flat list of renderable items, Home icon first.
    const items = useMemo<{ key: string; node: React.ReactNode }[]>(() => {
        const home = {
            key: "home",
            node: (
                <HomeFilledSharp
                    onClick={() => navigate('/')}
                    sx={{ fontSize: "12px", cursor: "pointer", color: "#666", display: "block" }}
                />
            ),
        };

        const crumbs = filteredBreadcrumbs.map(({ match, breadcrumb, key, noLink, linkTo }, index) => {
            const isLast = index === filteredBreadcrumbs.length - 1;
            // Condition Monitoring has no index page — it always redirects to the
            // asset dashboard, so render its crumb as non-clickable.
            const isConditionMonitoringParent = match.pathname.endsWith("/conditionMonitoring");
            // Notification Settings is a submenu parent without its own index page.
            const isNotificationSettingsParent = match.pathname.endsWith("/profile/notificationSettings");

            const node = isLast || (noLink && !linkTo) || isConditionMonitoringParent || isNotificationSettingsParent
                ? (
                    <Typography key={key} level="body-sm">
                        {breadcrumb}
                    </Typography>
                )
                : (
                    <Link
                        key={key}
                        to={linkTo ?? match.pathname}
                        style={{ textDecoration: "none", color: "#666" }}
                    >
                        <Typography level="body-sm">
                            {breadcrumb}
                        </Typography>
                    </Link>
                );

            return { key, node };
        });

        return [home, ...crumbs];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredBreadcrumbs, navigate]);

    const itemsKey = items.map((item) => item.key).join('|');

    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);
    const separatorRef = useRef<HTMLElement | null>(null);
    const ellipsisRef = useRef<HTMLElement | null>(null);

    // visibleCollapsibleStart: first index within the collapsible block that is
    // still visible. n means the whole collapsible block is hidden behind "…".
    const [collapseStart, setCollapseStart] = useState<number | null>(null);
    const [expanded, setExpanded] = useState(false);

    const n = items.length;
    const frontCount = Math.min(FRONT_COUNT, n);
    const endCount = Math.min(END_COUNT, Math.max(n - frontCount, 0));
    // Collapsible block is items[frontCount .. n-1-endCount].
    const collapsibleEnd = n - endCount; // exclusive

    // Reset expansion whenever the breadcrumb trail changes (e.g. navigating to
    // another asset) so it starts collapsed again.
    useLayoutEffect(() => {
        setExpanded(false);
    }, [itemsKey]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const recompute = () => {
            // No collapsible items → nothing to hide.
            if (collapsibleEnd <= frontCount) {
                setCollapseStart(null);
                return;
            }

            const available = container.clientWidth - WIDTH_RESERVE;
            const separatorWidth = separatorRef.current?.offsetWidth ?? 0;
            const widths = items.map((_item, index) => itemRefs.current[index]?.offsetWidth ?? 0);

            // Width of a row given its summed item widths and item count
            // (separators sit between adjacent items).
            const rowWidth = (itemsWidth: number, count: number) =>
                itemsWidth + separatorWidth * Math.max(count - 1, 0);

            const fullWidth = rowWidth(widths.reduce((sum, w) => sum + w, 0), widths.length);
            if (fullWidth <= available) {
                setCollapseStart(null);
                return;
            }

            // Minimum collapsed layout: front items + "…" + tail items.
            const ellipsisItemWidth = ellipsisRef.current?.offsetWidth ?? 0;
            const frontWidth = widths.slice(0, frontCount).reduce((sum, w) => sum + w, 0);
            const endWidth = widths.slice(collapsibleEnd).reduce((sum, w) => sum + w, 0);
            const baseItems = frontCount + 1 /* ellipsis */ + endCount;
            let used = rowWidth(frontWidth + ellipsisItemWidth + endWidth, baseItems);

            // Grow the visible suffix of the collapsible block from the last one
            // backward (so the earliest items after the client hide first).
            let start = collapsibleEnd; // whole collapsible block hidden
            for (let i = collapsibleEnd - 1; i >= frontCount; i--) {
                const next = used + separatorWidth + widths[i];
                if (next > available) break;
                used = next;
                start = i;
            }
            setCollapseStart(start);
        };

        recompute();
        const observer = new ResizeObserver(recompute);
        observer.observe(container);
        observer.observe(measure);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsKey, frontCount, endCount, collapsibleEnd]);

    // Truncated when at least one collapsible item (in [frontCount, collapseStart))
    // is hidden behind the "…".
    const truncated = !expanded && collapseStart !== null && collapseStart > frontCount;

    // Build the visible item list.
    const visibleItems: { key: string; node: React.ReactNode }[] = [];
    if (truncated) {
        visibleItems.push(...items.slice(0, frontCount));
        visibleItems.push({
            key: "__ellipsis__",
            node: (
                <Typography
                    level="body-sm"
                    onClick={() => setExpanded(true)}
                    sx={{
                        color: '#666',
                        cursor: 'pointer',
                        userSelect: 'none',
                        px: '4px',
                        borderRadius: '4px',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
                    }}
                >
                    …
                </Typography>
            ),
        });
        visibleItems.push(...items.slice(collapseStart as number));
    } else {
        visibleItems.push(...items);
    }

    const renderRow = (rowItems: { key: string; node: React.ReactNode }[]) => (
        rowItems.map((item, index) => (
            <React.Fragment key={item.key}>
                {index > 0 && <Typography level="body-sm" sx={separatorSx}>/</Typography>}
                {item.node}
            </React.Fragment>
        ))
    );

    return (
        <Box
            ref={containerRef}
            aria-label="breadcrumb"
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                flexWrap: expanded ? 'wrap' : 'nowrap',
                whiteSpace: expanded ? 'normal' : 'nowrap',
                overflow: 'hidden',
                minWidth: 0,
                rowGap: '4px',
                ...sx,
            }}
        >
            {renderRow(visibleItems)}

            {/* Hidden measurement row: all items at natural width, single line. */}
            <Box
                ref={measureRef}
                aria-hidden
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    visibility: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                }}
            >
                <Typography level="body-sm" ref={(el) => { ellipsisRef.current = el; }}>…</Typography>
                <Typography level="body-sm" sx={separatorSx} ref={(el) => { separatorRef.current = el; }}>/</Typography>
                {items.map((item, index) => (
                    <Box
                        key={item.key}
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                        ref={(el: HTMLElement | null) => { itemRefs.current[index] = el; }}
                    >
                        {item.node}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default Breadcrumbs;
